import Route from '@ember/routing/route';
import type Transition from '@ember/routing/transition';
import { service } from '@ember/service';
import type Store from '@ember-data/store';
import { subDays } from 'date-fns';
import type { PaginatedRecordArray } from 'garaje/infinity-models/v3-offset';
import type AnnouncementModel from 'garaje/models/announcement';
import type CurrentLocationService from 'garaje/services/current-location';
import type FeatureFlagsService from 'garaje/services/feature-flags';
import type MetricsService from 'garaje/services/metrics';
import type StateService from 'garaje/services/state';

import { getAnnouncementQueryParams } from './announcement-queries';
import type CommunicationsMessageLogListControllerBase from './controller-base';
import {
  emptyIncidentsResult,
  fetchIncidentsPage,
  type IncidentSummary,
  type IncidentsPageResult,
} from './incident-queries';

export const PAGE_SIZE = 25;
const SIX_MONTHS_IN_DAYS = 180;

export type AnnouncementsListModel = {
  mode: 'announcements';
  announcements: PaginatedRecordArray<AnnouncementModel>;
  startDate: Date;
  endDate: Date;
  incidentsByAnnouncementId: Map<string, IncidentSummary>;
};

export type IncidentsListModel = {
  mode: 'incidents';
  incidentsResult: IncidentsPageResult;
  startDate: Date;
  endDate: Date;
};

export type MessageLogListModel = AnnouncementsListModel | IncidentsListModel;

export default class CommunicationsMessageLogRouteBase extends Route {
  @service declare store: Store;
  @service declare state: StateService;
  @service declare currentLocation: CurrentLocationService;
  @service declare featureFlags: FeatureFlagsService;
  @service declare metrics: MetricsService;

  async model(): Promise<MessageLogListModel | void> {
    const currentDate = new Date();
    const startDate = subDays(currentDate, SIX_MONTHS_IN_DAYS);
    const endDate = currentDate;
    const locationId = this.currentLocation?.location?.id;

    const flagEnabled = this.featureFlags.isEnabled('emno-end-incident-web');

    if (flagEnabled && locationId) {
      try {
        const incidentsResult = await fetchIncidentsPage(locationId, startDate, endDate);
        return { mode: 'incidents', incidentsResult, startDate, endDate };
      } catch (error) {
        this.metrics.logMonitorError({ event: 'message_log_fetch_incidents_failed', error });
        return { mode: 'incidents', incidentsResult: emptyIncidentsResult(), startDate, endDate };
      }
    }

    const announcements = await this.loadAnnouncements(startDate, endDate);

    return { mode: 'announcements', announcements, startDate, endDate, incidentsByAnnouncementId: new Map() };
  }

  async loadAnnouncements(startDate: Date, endDate: Date): Promise<PaginatedRecordArray<AnnouncementModel>> {
    const queryParams = getAnnouncementQueryParams([this.currentLocation?.location?.id], startDate, endDate, PAGE_SIZE);

    try {
      const result = await this.store.query('announcement', queryParams);
      return result as PaginatedRecordArray<AnnouncementModel>;
    } catch (_error) {
      throw new Error(`Failed to load announcements`);
    }
  }

  setupController(
    controller: CommunicationsMessageLogListControllerBase,
    model: MessageLogListModel,
    transition: Transition,
    loadLocations: boolean = true,
  ): void {
    const { startDate, endDate } = model;
    super.setupController(controller, model, transition);
    controller.hydrateFromModel(model);
    void controller.initializeData.perform(startDate, endDate, loadLocations);
  }
}
